/**
 * Aktivitätszeile über dem Eingabefeld: wer im Hintergrund arbeitet. (19.10.2026, Hintergrund-Fach Etappe 2)
 *
 * Martin: „unten in einer eigenen Zeile, ob Vespera, Schmied oder Zimmermann im Hintergrund arbeitet.“ Eine schmale Zeile, die NUR
 * erscheint, wenn etwas läuft, und nur sagt, WER arbeitet und WORAN (Kurztext). Antippen öffnet die Seite „Hintergrund-Tasks“.
 *
 * Nichts Neues erfunden — zwei vorhandene Quellen:
 *   - Vespera: die Aufträge der Fortschrittskarte (Ereignis `job_fortschritt`, shared/job-fortschritt.ts), nur solange sie laufen.
 *   - Entwickler (Schmied, Zimmermann, Hüter): die vorhandene Statuszeile des Stroms (shared/schmied-status.ts), samt den Stufen des
 *     Wartens (60 s „hat noch nicht geantwortet“, 5 Min „antwortet nicht …“, `warteStufe`/`stufenText`). Die festen Zeilen „nicht
 *     verbunden“ / „hört gerade nicht zu“ sind KEINE Arbeit und stehen hier nicht.
 *
 * ⚠️ EINE Anzeige je Entwickler (Martin 20.10.2026 23:02: „hat deine Nachricht“ stand oben UND unten): steht die Warteblase des
 *    Entwicklers im Verlauf (lib/schmied-warteblase.ts `sichtbar`, nicht ausgeblendet), lässt die Zeile ihn aus — die Blase hat Vorrang. Ohne Blase
 *    (Strom neu verbunden, Blase abgelaufen oder per „Ausblenden“ versteckt) zeigt wieder die Zeile den Zustand.
 * ⚠️ Keine Liste, keine Details: eine Zeile, einzeilig gekürzt (CSS); der volle Wortlaut steht im Namen des Knopfs für Screenreader.
 * ⚠️ Kein Chat-Beitrag, nichts im Prompt, nichts im Verlauf. Alle Texte nur über textContent.
 * ⚠️ Zeitgeber nur, solange mindestens ein Eintrag da ist (Stufen und Verfall der Entwickler-Zeilen).
 */
import { entwickler, type EntwicklerId } from "../../shared/entwickler.js";
import type { JobFortschritt } from "../../shared/job-fortschritt.js";
import {
  SCHMIED_STATUS_UEBERGABE_NICHT_TEXT,
  ZIMMERMANN_STATUS_NICHT_TEXT,
  ohneZeitzaehler,
  stufenText,
  warteStufe,
  type SchmiedStatusStand,
} from "../../shared/schmied-status.js";

export const AKTIVITAET_TAKT_MS = 15_000;
export const VESPERA_NAME = "Vespera";
/** Die festen Zeilen, die „nicht da“ sagen — kein Arbeiten. */
const KEIN_ARBEITEN: readonly string[] = [ZIMMERMANN_STATUS_NICHT_TEXT, SCHMIED_STATUS_UEBERGABE_NICHT_TEXT];

/** So viel später darf der Verfall liegen, ohne als neu gesetzt zu gelten (Takt, Netzlaufzeit). */
const NEU_GESETZT_TOLERANZ_MS = 2000;

interface DevEintrag {
  text: string;
  seit: number;
  bis: number;
}

export interface AktivitaetsZeile {
  setzeJob(job: JobFortschritt): void;
  /** Der Stand aus dem Strom; `text` null (oder eine „nicht da“-Zeile) nimmt den Entwickler heraus. */
  setzeEntwickler(id: EntwicklerId, stand: SchmiedStatusStand): void;
  /** Der Strom hat sich neu verbunden: Entwickler-Zeilen kommen vom Server neu, die laufenden Aufträge liefert der Server gleich nach. */
  verbindungNeu(): void;
  /** Neu zeichnen (die Warteblase eines Entwicklers ist erschienen, verschwunden oder ausgeblendet). */
  neuZeichnen(): void;
  /** Der Satz, der gerade steht ("" = Zeile versteckt). */
  satz(): string;
  stopp(): void;
}

/** Die Wörter der Zeile aus dem Stand beider Quellen. Rein. */
export function aktivitaetsSatz(jobs: readonly { titel: string }[], devs: ReadonlyMap<EntwicklerId, { text: string }>, jetzt: number, seit: ReadonlyMap<EntwicklerId, number>): string {
  const teile: string[] = [];
  // Steht Vespera allein da, nennt die Zeile den Titel; sonst nur die Zahl — auf 390 Punkten bliebe für die Entwickler sonst kein Platz.
  if (jobs.length === 1) teile.push(`${VESPERA_NAME}: ${devs.size === 0 ? jobs[0].titel : "1 Auftrag"}`);
  else if (jobs.length > 1) teile.push(`${VESPERA_NAME}: ${jobs.length} Aufträge`);
  for (const [id, e] of devs) {
    const stufe = warteStufe(e.text, jetzt - (seit.get(id) ?? jetzt));
    // 26.10.2026: abgelaufene „keine Antwort“-Meldungen laufen nicht mehr — die Zeile lässt sie aus (Blase zeigt sie ausgegraut).
    if (stufe === "abgelaufen") continue;
    teile.push(`${entwickler(id).name}: ${stufenText(e.text, stufe)}`);
  }
  return teile.join(" · ");
}

export function baueAktivitaetsZeile(
  knopf: HTMLElement,
  oeffne: () => void,
  jetzt: () => number = Date.now,
  /** Zeigt die Warteblase dieses Entwicklers den Zustand schon? Dann steht er hier nicht noch einmal. */
  hatBlase: (id: EntwicklerId) => boolean = () => false,
): AktivitaetsZeile {
  const doc = knopf.ownerDocument;
  const jobs = new Map<string, JobFortschritt>();
  const devs = new Map<EntwicklerId, DevEintrag>();
  let takt: ReturnType<typeof setInterval> | null = null;
  let letzter = "";

  const text = (): HTMLElement => {
    let t = knopf.querySelector<HTMLElement>(".aktivitaet-text");
    if (!t) {
      const punkt = doc.createElement("span");
      punkt.className = "aktivitaet-punkt";
      punkt.setAttribute("aria-hidden", "true");
      t = doc.createElement("span");
      t.className = "aktivitaet-text";
      knopf.replaceChildren(punkt, t);
    }
    return t;
  };

  const zeichne = (): void => {
    const j = jetzt();
    for (const [id, e] of [...devs]) if (e.bis <= j) devs.delete(id);
    const laufend = [...jobs.values()].filter((x) => x.status === "running");
    const sichtbar = new Map([...devs].filter(([id]) => !hatBlase(id)));
    const seit = new Map([...sichtbar].map(([id, e]) => [id, e.seit] as const));
    const satz = aktivitaetsSatz(laufend, sichtbar, j, seit);
    if (satz !== letzter) {
      letzter = satz;
      text().textContent = satz;
      knopf.hidden = satz === "";
      if (satz) knopf.setAttribute("aria-label", `Im Hintergrund: ${satz}. Öffnet die Hintergrund-Tasks.`);
      else knopf.removeAttribute("aria-label");
    }
    const noetig = laufend.length > 0 || devs.size > 0;
    if (noetig && !takt) takt = setInterval(zeichne, AKTIVITAET_TAKT_MS);
    else if (!noetig && takt) {
      clearInterval(takt);
      takt = null;
    }
  };

  knopf.addEventListener("click", () => oeffne());
  knopf.hidden = true;

  return {
    setzeJob(job) {
      if (job.status === "running") jobs.set(job.id, job);
      else jobs.delete(job.id);
      zeichne();
    },
    setzeEntwickler(id, stand) {
      if (stand.text === null || KEIN_ARBEITEN.includes(ohneZeitzaehler(stand.text))) devs.delete(id);
      else {
        const alt = devs.get(id);
        const j = jetzt();
        // Gleicher Text, aber der Verfall liegt SPÄTER als zuvor: der Server hat neu gesetzt (neue Nachricht) — die Wartezeit beginnt von vorn.
        const neu = alt !== undefined && ohneZeitzaehler(alt.text) === ohneZeitzaehler(stand.text) && j + stand.restMs <= alt.bis + NEU_GESETZT_TOLERANZ_MS;
        devs.set(id, { text: stand.text, seit: neu && alt ? alt.seit : j, bis: j + stand.restMs });
      }
      zeichne();
    },
    verbindungNeu() {
      jobs.clear();
      devs.clear();
      zeichne();
    },
    neuZeichnen: zeichne,
    satz: () => letzter,
    stopp() {
      if (takt) clearInterval(takt);
      takt = null;
    },
  };
}
